import IPPageNumber from "app/pages/InteractiveProposal/InteractiveProposalDownload/IPPageNuber";
import HexagonImg from "app/pages/InteractiveProposal/images/hexagonImg.png";
import { getSessionStore } from "app/utils/sessionStore";
import {
  createGradient,
  formatToCompactNumber,
  getThemeColors,
  getUSANumberFormat,
  projectionSDOptions,
} from "app/utils/utils";
import { getColorVariations } from "app/utils/colorUtils";
import { Chart } from "primereact/chart";
import {
  barGraphOptionsFunding,
  lineChartOptions,
} from "app/uicomponents/macrocomponents/PlansSummary/config";

interface PerformanceMetrics {
  actualAccountValue: number;
  actualSurrenderValue: number;
  actualDeathBenefit: number;
  actualFinancingBalance: number;
  totalOutsideAssets: number;
  interestCredits: number;
}

interface PerformanceRow {
  planYear: number;
  preCredit: PerformanceMetrics;
  postCredit: PerformanceMetrics;
}

interface IRCPerformancePageProps {
  performance: PerformanceRow[];
  creditType?: string;
  pageNumber: string | number;
  /** "charts" renders only the 4 chart grid; "table" renders only the data table */
  mode: "charts" | "table";
}

const tableHeaders = [
  { label: "Plan Year" },
  { label: "Account Value" },
  { label: "Surrender Value" },
  { label: "Death Benefit" },
  { label: "Income" },
  { label: "Financing Balance" },
  { label: "Interest Credits" },
];

const IRCPerformancePage = ({
  performance,
  creditType = "postCredit",
  pageNumber,
  mode,
}: IRCPerformancePageProps) => {
  const { branding } = getSessionStore("lookUpResponse");
  const { primaryColor, ipTooltipColor, secondaryColor } = getThemeColors(branding);

  const planYears = performance.map((r) => r.planYear);
  const getMetrics = (r: PerformanceRow): PerformanceMetrics =>
    creditType === "postCredit" ? r.postCredit : r.preCredit;

  const incomeValues = performance.map((r) => getMetrics(r)?.totalOutsideAssets);
  const accountValues = performance.map((r) => getMetrics(r)?.actualAccountValue);
  const surrenderValues = performance.map((r) => getMetrics(r)?.actualSurrenderValue);
  const deathBenefits = performance.map((r) => getMetrics(r)?.actualDeathBenefit);

  const latestIdx = performance.length - 1;
  const latestMetrics = latestIdx >= 0 ? getMetrics(performance[latestIdx]) : null;

  const makeLineChart = (label: string, data: number[], colorIdx: number) => ({
    labels: planYears,
    datasets: [
      {
        label,
        fill: true,
        borderColor: projectionSDOptions(primaryColor)[colorIdx].indicatorColor,
        backgroundColor: (context: any) =>
          createGradient(context, projectionSDOptions(primaryColor)[colorIdx].indicatorColor),
        yAxisID: "y",
        tension: 0.4,
        data,
      },
    ],
  });

  const incomeChart = {
    labels: planYears,
    datasets: [
      {
        label: "Income",
        backgroundColor: projectionSDOptions(primaryColor)[0].indicatorColor,
        data: incomeValues,
      },
    ],
  };

  const accountChart = makeLineChart("Account Value", accountValues, 1);
  const surrenderChart = makeLineChart("Surrender Value", surrenderValues, 2);
  const deathBenefitChart = makeLineChart("Death Benefit", deathBenefits, 3);

  const colorVariations = getColorVariations(secondaryColor, tableHeaders.length).reverse();
  const creditLabel = creditType === "postCredit" ? "Post-Credit" : "Pre-Credit";

  const ChartCard = ({
    title,
    value,
    chartData,
    type = "line",
  }: {
    title: string;
    value: string;
    chartData: any;
    type?: "line" | "bar";
  }) => (
    <div className="flex-1">
      <div className="flex justify-between conthrax-font">
        <p className="font-bold text-gray-800 text-xs">{title}</p>
        <span className="text-xs font-bold text-gray-500">{value}</span>
      </div>
      <Chart
        type={type}
        height="200px"
        width="240px"
        data={chartData}
        options={
          type === "bar"
            ? barGraphOptionsFunding(false, ipTooltipColor, "Plan Year", "", "Plan Year")
            : lineChartOptions(false, ipTooltipColor, "Plan Year", "Plan Year")
        }
      />
    </div>
  );

  return (
    <div className="flex flex-col h-full relative min-h-screen">
      <div className="relative z-20 px-[2em] w-full max-w-[56em] mx-auto p-[1.75em]">
        <h1 className="text-center text-2xl font-bold mb-4 conthrax-font interactive-proposal-text">
          Performance ({creditLabel})
        </h1>

        {mode === "charts" && (
          <>
            {/* Row 1: Income (bar) + Account Value */}
            <div className="flex gap-6 mb-6">
              <ChartCard
                title="Income"
                value={
                  latestMetrics ? `$${formatToCompactNumber(latestMetrics.totalOutsideAssets)}` : ""
                }
                chartData={incomeChart}
                type="bar"
              />
              <ChartCard
                title="Account Value"
                value={
                  latestMetrics ? `$${formatToCompactNumber(latestMetrics.actualAccountValue)}` : ""
                }
                chartData={accountChart}
              />
            </div>

            {/* Row 2: Surrender Value + Death Benefit */}
            <div className="flex gap-6">
              <ChartCard
                title="Surrender Value"
                value={
                  latestMetrics
                    ? `$${formatToCompactNumber(latestMetrics.actualSurrenderValue)}`
                    : ""
                }
                chartData={surrenderChart}
              />
              <ChartCard
                title="Death Benefit"
                value={
                  latestMetrics ? `$${formatToCompactNumber(latestMetrics.actualDeathBenefit)}` : ""
                }
                chartData={deathBenefitChart}
              />
            </div>
          </>
        )}

        {mode === "table" && (
          <table className="w-full border-collapse">
            <thead className="text-white">
              <tr>
                {tableHeaders.map((h, i) => (
                  <th
                    key={i}
                    className="p-1 text-center pdf_table_border border-white font-semibold"
                    style={{ backgroundColor: colorVariations[i], fontSize: "9px" }}
                  >
                    {h.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {performance.map((row, i) => {
                const m = getMetrics(row);
                return (
                  <tr
                    key={i}
                    style={{ backgroundColor: "#EEEEEE", color: "#000000", fontSize: "9px" }}
                  >
                    <td className="p-1 text-center pdf_table_border border-white">
                      {row.planYear}
                    </td>
                    <td className="p-1 text-center pdf_table_border border-white">
                      ${getUSANumberFormat(m.actualAccountValue, false)}
                    </td>
                    <td className="p-1 text-center pdf_table_border border-white">
                      ${getUSANumberFormat(m.actualSurrenderValue, false)}
                    </td>
                    <td className="p-1 text-center pdf_table_border border-white">
                      ${getUSANumberFormat(m.actualDeathBenefit, false)}
                    </td>
                    <td className="p-1 text-center pdf_table_border border-white">
                      ${getUSANumberFormat(m.totalOutsideAssets, false)}
                    </td>
                    <td className="p-1 text-center pdf_table_border border-white">
                      ${getUSANumberFormat(m.actualFinancingBalance, false)}
                    </td>
                    <td className="p-1 text-center pdf_table_border border-white">
                      ${getUSANumberFormat(m.interestCredits, false)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="absolute bottom-0 left-0 w-full">
        <div
          className="h-[350px] bg-cover bg-center"
          style={{ backgroundImage: `url(${HexagonImg})` }}
        />
        <div className="px-[4em] w-full max-w-[56em] mx-auto p-[1.75em] pt-1">
          <IPPageNumber number={pageNumber} />
        </div>
      </div>
    </div>
  );
};

export default IRCPerformancePage;
